import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { PageLocation } from "../model/pageLocation";

interface UiContextState {
  location: PageLocation | null;
  publish: (location: PageLocation | null) => void;
}

const UiContext = createContext<UiContextState | undefined>(undefined);

/**
 * Carries what the current screen has on it to the dock, which is mounted in the layout and cannot
 * see the routed page's state any other way.
 */
export function UiContextProvider({ children }: { children: ReactNode }) {
  const [location, setLocation] = useState<PageLocation | null>(null);
  const value = useMemo<UiContextState>(() => ({ location, publish: setLocation }), [location]);
  return <UiContext.Provider value={value}>{children}</UiContext.Provider>;
}

/**
 * Publish what this screen has in view, and clear it on unmount. This is the one publishing effect:
 * the location hooks compute a snapshot in render and hand it here.
 *
 * A no-op outside a provider, deliberately: publishing is a courtesy a page pays the dock, and a
 * screen rendered without the provider (a focused unit test) should render, not crash.
 */
export function usePublishUiContext(location: PageLocation | null): void {
  const publish = useContext(UiContext)?.publish;
  // Serialized, so a page that rebuilds an equal object on every render does not re-publish.
  const serialized = location === null ? null : JSON.stringify(location);
  useEffect(() => {
    if (publish === undefined) return;
    publish(serialized === null ? null : (JSON.parse(serialized) as PageLocation));
    return () => publish(null);
  }, [publish, serialized]);
}

/** @returns what the current screen published, or null when it published nothing. */
export function useUiContext(): PageLocation | null {
  return useContext(UiContext)?.location ?? null;
}
